import React from 'react';
import { SectionHeader } from '../ui/SectionHeader';
import { loanOfficers } from '../data/mockData';
export function ConversionSection() {
  // Calculate company averages for the funnel
  const averages = {
    dialToConv: Math.round(loanOfficers.reduce((acc, lo) => acc + lo.conversion.dialToConv, 0) / loanOfficers.length),
    convToPull: Math.round(loanOfficers.reduce((acc, lo) => acc + lo.conversion.convToPull, 0) / loanOfficers.length),
    pullToApp: Math.round(loanOfficers.reduce((acc, lo) => acc + lo.conversion.pullToApp, 0) / loanOfficers.length),
    appToSubmit: Math.round(loanOfficers.reduce((acc, lo) => acc + lo.conversion.appToSubmit, 0) / loanOfficers.length),
    submitToLock: Math.round(loanOfficers.reduce((acc, lo) => acc + lo.conversion.submitToLock, 0) / loanOfficers.length),
    lockToFund: Math.round(loanOfficers.reduce((acc, lo) => acc + lo.conversion.lockToFund, 0) / loanOfficers.length),
    pullToFund: Math.round(loanOfficers.reduce((acc, lo) => acc + lo.conversion.pullToFund, 0) / loanOfficers.length)
  };
  // Helper for color coding cells
  const getRateColor = (value: number, target: number) => {
    if (value >= target) return 'bg-green-100 text-green-800';
    if (value >= target * 0.9) return 'bg-amber-100 text-amber-800';
    return 'bg-red-100 text-red-800';
  };
  // Funnel steps configuration
  const funnelSteps = [{
    label: 'Dial → Conv',
    value: averages.dialToConv,
    width: '100%',
    color: 'bg-blue-100'
  }, {
    label: 'Conv → Pull',
    value: averages.convToPull,
    width: '85%',
    color: 'bg-blue-200'
  }, {
    label: 'Pull → App',
    value: averages.pullToApp,
    width: '70%',
    color: 'bg-blue-300'
  }, {
    label: 'App → Submit',
    value: averages.appToSubmit,
    width: '55%',
    color: 'bg-blue-400'
  }, {
    label: 'Submit → Lock',
    value: averages.submitToLock,
    width: '40%',
    color: 'bg-blue-500'
  }, {
    label: 'Lock → Fund',
    value: averages.lockToFund,
    width: '25%',
    color: 'bg-blue-600'
  }];
  return <section className="space-y-8 animate-in fade-in duration-500" data-id="element-53">
      <SectionHeader title="Conversion (Skill)" description="Analyzing the efficiency of the sales funnel." data-id="element-54" />

      {/* Visual Funnel */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-55">
        <h3 className="text-sm font-semibold text-slate-500 mb-6 uppercase tracking-wider" data-id="element-56">
          Company Average Funnel
        </h3>
        <div className="flex flex-col gap-2 max-w-3xl mx-auto" data-id="element-57">
          {funnelSteps.map((step, idx) => <div key={idx} className="relative h-12 flex items-center group" data-id="element-58">
              <div className={`absolute left-0 top-0 h-full rounded-r-lg transition-all duration-500 ${step.color} opacity-80 group-hover:opacity-100`} style={{
            width: step.width
          }} data-id="element-59" />
              <div className="relative z-10 flex justify-between w-full px-4 text-slate-800 font-medium" data-id="element-60">
                <span data-id="element-61">{step.label}</span>
                <span className="font-bold" data-id="element-62">{step.value}%</span>
              </div>
            </div>)}
        </div>
      </div>

      {/* Conversion Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-63">
        <div className="overflow-x-auto" data-id="element-64">
          <table className="w-full text-sm text-center" data-id="element-65">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-66">
              <tr data-id="element-67">
                <th className="px-4 py-4 text-left" data-id="element-68">Loan Officer</th>
                <th className="px-4 py-4" data-id="element-69">Dial → Conv</th>
                <th className="px-4 py-4" data-id="element-70">Conv → Pull</th>
                <th className="px-4 py-4" data-id="element-71">Pull → App</th>
                <th className="px-4 py-4" data-id="element-72">App → Submit</th>
                <th className="px-4 py-4" data-id="element-73">Submit → Lock</th>
                <th className="px-4 py-4" data-id="element-74">Lock → Fund</th>
                <th className="px-4 py-4 bg-slate-100 border-l border-slate-200 font-bold text-slate-700" data-id="element-75">
                  Pull → Fund (Effectiveness)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-76">
              {/* Company Average Row */}
              <tr className="bg-slate-50 font-semibold text-slate-700 border-b border-slate-200" data-id="element-77">
                <td className="px-4 py-3 text-left" data-id="element-78">Company Avg</td>
                <td className="px-4 py-3" data-id="element-79">{averages.dialToConv}%</td>
                <td className="px-4 py-3" data-id="element-80">{averages.convToPull}%</td>
                <td className="px-4 py-3" data-id="element-81">{averages.pullToApp}%</td>
                <td className="px-4 py-3" data-id="element-82">{averages.appToSubmit}%</td>
                <td className="px-4 py-3" data-id="element-83">{averages.submitToLock}%</td>
                <td className="px-4 py-3" data-id="element-84">{averages.lockToFund}%</td>
                <td className="px-4 py-3 bg-slate-100 border-l border-slate-200" data-id="element-85">
                  {averages.pullToFund}%
                </td>
              </tr>

              {/* LO Rows */}
              {loanOfficers.map(lo => <tr key={lo.id} className="hover:bg-slate-50 transition-colors" data-id="element-86">
                  <td className="px-4 py-4 text-left font-medium text-slate-900" data-id="element-87">
                    {lo.name}
                  </td>
                  <td className="px-4 py-4" data-id="element-88">
                    <span className={`px-2 py-1 rounded-md text-xs font-bold ${getRateColor(lo.conversion.dialToConv, 30)}`} data-id="element-89">
                      {lo.conversion.dialToConv}%
                    </span>
                  </td>
                  <td className="px-4 py-4" data-id="element-90">
                    <span className={`px-2 py-1 rounded-md text-xs font-bold ${getRateColor(lo.conversion.convToPull, 25)}`} data-id="element-91">
                      {lo.conversion.convToPull}%
                    </span>
                  </td>
                  <td className="px-4 py-4" data-id="element-92">
                    <span className={`px-2 py-1 rounded-md text-xs font-bold ${getRateColor(lo.conversion.pullToApp, 70)}`} data-id="element-93">
                      {lo.conversion.pullToApp}%
                    </span>
                  </td>
                  <td className="px-4 py-4" data-id="element-94">
                    <span className={`px-2 py-1 rounded-md text-xs font-bold ${getRateColor(lo.conversion.appToSubmit, 80)}`} data-id="element-95">
                      {lo.conversion.appToSubmit}%
                    </span>
                  </td>
                  <td className="px-4 py-4" data-id="element-96">
                    <span className={`px-2 py-1 rounded-md text-xs font-bold ${getRateColor(lo.conversion.submitToLock, 80)}`} data-id="element-97">
                      {lo.conversion.submitToLock}%
                    </span>
                  </td>
                  <td className="px-4 py-4" data-id="element-98">
                    <span className={`px-2 py-1 rounded-md text-xs font-bold ${getRateColor(lo.conversion.lockToFund, 90)}`} data-id="element-99">
                      {lo.conversion.lockToFund}%
                    </span>
                  </td>
                  <td className="px-4 py-4 bg-slate-50 border-l border-slate-200" data-id="element-100">
                    <span className={`px-2 py-1 rounded-md text-xs font-bold ${getRateColor(lo.conversion.pullToFund, 40)}`} data-id="element-101">
                      {lo.conversion.pullToFund}%
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </section>;
}